import React, { PureComponent, createRef } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import { injectIntl } from 'react-intl'
import withToast from 'src/components/Toast'
import { Box, Text, Flex } from '@chakra-ui/react'
import cx from 'classnames'
import MarkerClusterer from '@googlemaps/markerclustererplus'
import {
  GoogleMapApiWrapper,
  GoogleMapFindBar,
  GoogleMapList,
  PlaceRating,
} from 'src/components/Maps/KioskGoogleMap'
import { LinkButton } from 'src/components/LinkButton'
import { Loader } from 'src/components/Loader'
import SvgIcon from 'src/components/SvgIcon'
import {
  geoLocationExists,
  logError,
  logMessage,
  getApiSrcImagesUrl,
  isEmpty,
  getKioskWindow,
  getKioskDocument,
} from 'src/utils/helpers'
import Storage from 'src/utils/storage'
import googleMapStyle from 'src/components/Maps/styles.js'
import intlMessagesApp from 'src/modules/App/translations'
import intlMessages from 'src/components/Maps/translations'
import { LocationType } from 'src/app/constants'
import styles from './GoogleMapContainer.module.less'
import FontAwesomeIconComponent from 'src/components/FontAwesomeIcon/index'

class GoogleMapContainer extends PureComponent {
  state = {
    map: null,
    places: [],
    selectedPlace: null,
    markerCluster: null,
    myLocationType: 'unknown',
    isBackdropVisible: false,
  }

  constructor(props) {
    super(props)

    this.windowResizeTimeout = null
    this.window = getKioskWindow()
    this.document = getKioskDocument()

    this.googleMapContainerRef = createRef()
    this.googleMapRef = createRef()
    this.selectedPlaceRef = createRef()
    this.currentLocationMarker = null
    this.currentLocationRadiusCircle = null
    // Collecting map zoom and position so we can "undo" to previous map view
    this.mapPositions = []
    this.selectedMarker = null
    this.selectedMarkerIcon = null
    this.apiSrcImagesUrl = getApiSrcImagesUrl()
    // Leave some space for search bar at the top
    this.mapPanOffsetTop = 40
    this.mapPanVertical = 0
    // 10 km
    this.searchRadius = 10000
    // 10 minutes (in seconds)
    this.geoLocationCacheSeconds = 10 * 60
    // 5 minutes (in seconds)
    this.approximateLocationCacheSeconds = 5 * 60
    // Holds latest location error
    this.locationError = {}
  }

  componentDidMount() {
    // Set map height on load
    this.setMapFullHeightStyleProperty()

    // Listen to the window resize event to update map height
    this.window.addEventListener('resize', this.setMapFullHeightStyleProperty)

    this.initMap()
  }

  componentWillUnmount() {
    this.window.removeEventListener('resize', this.setMapFullHeightStyleProperty)
  }

  /**
   * Set style property, which will be picked up in CSS
   *
   * @returns {void}
   */
  setMapFullHeightStyleProperty = () => {
    if (this.windowResizeTimeout) {
      clearTimeout(this.windowResizeTimeout)
    }

    // Run with 200ms delay
    this.windowResizeTimeout = setTimeout(() => {
      // First we get the viewport height and we multiple it by 1% to get a value for a vh unit
      const vh = this.window.innerHeight * 0.01

      // Then we set the value in the --map-full-height custom property to the root of the document
      this.document.documentElement.style.setProperty('--map-full-height', `${vh}px`)
    }, 200)
  }

  /**
   * Initialize google map, find places and set markers for those places.
   *
   * @returns {void}
   */
  initMap = async () => {
    try {
      const { google, isWidget } = this.props

      // If not prepared in GoogleMapApiWrapper
      if (!(google && google.maps)) {
        logError('google.maps not available in props on init map', null, 0)
        return false
      }

      if (!(this.googleMapRef && this.googleMapRef.current)) {
        logError('Google map DOM element not found')
        return false
      }

      if (isWidget) {
        // Set google in main window, because it's only loaded in widget's window (iframe)
        window.google = google
      }

      // TODO: PAT-2318 Try to show "backdrop" while location prompt is opened

      // this.setState({
      //   isBackdropVisible: true,
      // })

      // Get current user's location
      const location = await this.getLocationDetails(true)

      const latLng = location.latLng

      // this.setState({
      //   isBackdropVisible: false,
      // })

      // Default to Toronto (it will be zoomed out)
      const defaultLocation = {
        lat: 43.6532,
        lng: -79.3832,
      }

      // Initialize google map
      const map = new google.maps.Map(this.googleMapRef.current, {
        center: latLng || defaultLocation,
        zoom: 3,
        disableDefaultUI: true,
        zoomControl: true,
        // "cooperative" in iframe (widget) or "greedy" elsewhere
        gestureHandling: 'auto',
        disableDoubleClickZoom: false,
        styles: googleMapStyle,
      })

      this.setState(
        {
          map: map,
          myLocationType: location.locationType,
        },
        () => {
          if (!latLng) {
            return false
          }

          // Search and place all markers
          this.onLocationFound(map, latLng)
        },
      )
    } catch (e) {
      logError('Init map', e)
    }
  }

  /**
   * When initial location is found or when changed with search field.
   *
   * @param {object} map Google map instance
   * @param {object<{ lat: number, lng: number}>} latLng
   * @param {function} afterLoadCallback
   * @returns {void}
   */
  onLocationFound = (map, latLng, afterLoadCallback = () => {}) => {
    // Add marker/pin for current location
    this.setCurrentLocationMarker(map, latLng)

    // Search and add markers
    this.searchAndShowResults(map, latLng, (withResults = false, searchStatus = '') => {
      // If there are no results
      if (!withResults && searchStatus !== 'OVER_QUERY_LIMIT') {
        // Clear places from the list
        this.setState(
          {
            places: [],
          },
          () => {
            // Clear current markers
            this.clearMarkers()
          },
        )

        // Zoom in to found location and show search radius
        this.handleLocationWithoutResults(map, latLng)
      }

      if (typeof afterLoadCallback === 'function') {
        afterLoadCallback()
      }
    })

    // Close place popup
    this.closeSelectedPlace()
  }

  /**
   * If we couldn't find location from the search
   */
  onLocationNotFound = () => {
    const { intl, toast } = this.props

    toast({
      title: intl.formatMessage(intlMessagesApp.appErrorTitle),
      description: intl.formatMessage(intlMessages.noLocationFoundMessage),
      status: 'warning',
    })
  }

  /**
   * As soon as we change location in the search.
   */
  onNewLocationStart = () => {
    this.clearCurrentLocationRadiusCircle()
  }

  /**
   * Clear current location's search radius circle.
   */
  clearCurrentLocationRadiusCircle = () => {
    try {
      // Clear existing radius
      if (
        this.currentLocationRadiusCircle &&
        typeof this.currentLocationRadiusCircle.setMap === 'function'
      ) {
        this.currentLocationRadiusCircle.setMap(null)
      }
    } catch (e) {
      logError('Zoom in to found location', e)
    }
  }

  /**
   * Used to zoom in to found location if no results were found.
   *
   * @param {object} map Google map instance
   * @param {object<{ lat: number, lng: number}>} latLng
   * @return {boolean}
   */
  handleLocationWithoutResults = (map, latLng) => {
    try {
      const { google } = this.props

      if (!map || !latLng) {
        return false
      }

      // If not prepared in GoogleMapApiWrapper
      if (!(google && google.maps)) {
        logError('google.maps not available in props on zoom in to found location', null, 0)
        return false
      }

      this.clearCurrentLocationRadiusCircle()

      this.currentLocationRadiusCircle = new google.maps.Circle({
        map: map,
        center: latLng,
        radius: this.searchRadius,
        fillColor: '#1E2A4F',
        strokeColor: '#1E2A4F',
        fillOpacity: 0.1,
        strokeOpacity: 0.25,
        strokeWeight: 2,
      })

      map.fitBounds(this.currentLocationRadiusCircle.getBounds())

      return true
    } catch (e) {
      logError('Zoom in to found location with no results', e)
      return false
    }
  }

  /**
   * Search and show results on the map.
   *
   * @param {object} map Google map instance
   * @param {object<{ lat: number, lng: number}>} latLng
   * @param {function} afterLoadCallback
   * @returns {boolean}
   */
  searchAndShowResults = (map, latLng, afterLoadCallback) => {
    try {
      const { intl, mapFilterValue, mapFilterType, google, toast } = this.props

      // If not prepared in GoogleMapApiWrapper
      if (!(google && google.maps)) {
        logError('google.maps not available in props on search and show results', null, 0)
        afterLoadCallback()
        return false
      }

      if (!latLng) {
        logError('latLng not received on search and show results', null, 0)
        afterLoadCallback()
        return false
      }

      // Create places service
      const service = new google.maps.places.PlacesService(map)

      service.nearbySearch(
        {
          // Circle's radius in meters
          radius: this.searchRadius,
          location: latLng,
          openNow: true,
          // If type is "q" (query)
          keyword: mapFilterType === 'q' ? mapFilterValue : null,
          // If type is "c" (category)
          type: mapFilterType === 'c' ? mapFilterValue : null,
        },
        async (results, status) => {
          if (status !== google.maps.places.PlacesServiceStatus.OK) {
            if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
              toast({
                title: intl.formatMessage(intlMessagesApp.appErrorTitle),
                description: intl.formatMessage(intlMessages.noResultsFoundMessage),
                status: 'warning',
              })
            } else {
              logError('Search places not ok', { status })
            }

            afterLoadCallback(false, status)
            return false
          }

          // Get icons and other additional data
          const places = await this.attachAdditionalPlaceData(map, results)

          // Create markers for each place and attach it under "mapMarker" property for every place
          const placesWithMarkers = this.createMarkers(map, places, latLng)

          const hasPlaces = placesWithMarkers && placesWithMarkers.length > 0

          this.setState(
            {
              places: hasPlaces ? placesWithMarkers : [],
            },
            () => {
              afterLoadCallback(hasPlaces, status)
            },
          )
        },
      )

      return true
    } catch (e) {
      logError('Search and show results', e)
      afterLoadCallback()
      return false
    }
  }

  /**
   * Go through all found places and attach additional data.
   *
   * @param {object} map Google map instance
   * @param {array} places Places found for search query
   * @returns {array} places Return received places but with additional data
   */
  attachAdditionalPlaceData = async (map, places) => {
    try {
      const { mapFilterValue, google } = this.props

      // If not prepared in GoogleMapApiWrapper
      if (!(google && google.maps)) {
        logError('google.maps not available in props on attach additional place data', null, 0)
        return places
      }

      const selectedIcon = {
        url: `${this.apiSrcImagesUrl}/map/location-pin-plain-star.svg`,
        size: new google.maps.Size(44, 58),
        scaledSize: new google.maps.Size(44, 58),
        // Where pin points to, bottom middle: ({halfOfWidth}, {fullHeight})
        anchor: new google.maps.Point(22, 58),
      }

      const service = new google.maps.places.PlacesService(map)

      // Loop through all found places
      for (let i = 0, place; (place = places[i]); i++) {
        // Default marker icon
        let markerIcon = {
          url: place.icon,
          scaledSize: new google.maps.Size(25, 25),
        }

        const svgIconSize = new google.maps.Size(34, 46)
        const svgIconScaledSize = new google.maps.Size(34, 46)
        const svgIconScaledSizeGas = new google.maps.Size(36, 46)
        // Where pin points to, bottom middle: ({halfOfWidth}, {fullHeight})
        const svgIconAnchor = new google.maps.Point(17, 46)

        let placeIconName = null

        if (mapFilterValue.includes('gas')) {
          markerIcon = {
            url: `${this.apiSrcImagesUrl}/map/location-pin-plain-gas.svg`,
            size: svgIconSize,
            scaledSize: svgIconScaledSizeGas,
            anchor: svgIconAnchor,
          }

          placeIconName = ['far', 'gas-pump']
        }
        if (mapFilterValue.includes('locksmith')) {
          markerIcon = {
            url: `${this.apiSrcImagesUrl}/map/location-pin-plain-locksmith.svg`,
            size: svgIconSize,
            scaledSize: svgIconScaledSize,
            anchor: svgIconAnchor,
          }

          placeIconName = ['far', 'key']
        }
        if (mapFilterValue.includes('tow')) {
          markerIcon = {
            url: `${this.apiSrcImagesUrl}/map/location-pin-plain-tow-truck.svg`,
            size: svgIconSize,
            scaledSize: svgIconScaledSize,
            anchor: svgIconAnchor,
          }

          placeIconName = ['far', 'truck-tow']
        }
        if (mapFilterValue.includes('police')) {
          markerIcon = {
            url: `${this.apiSrcImagesUrl}/map/location-pin-plain-police.svg`,
            size: svgIconSize,
            scaledSize: svgIconScaledSize,
            anchor: svgIconAnchor,
          }

          placeIconName = ['far', 'user-police-tie']
        }

        // Get more details for this place (currently just phone number)
        const placeDetails = await this.getPlaceDetails(google, service, place.place_id)

        places[i] = {
          ...places[i],
          ...placeDetails,
          markerIconSelected: selectedIcon,
          markerIcon: markerIcon,
          placeIconName: placeIconName,
        }
      }

      return places
    } catch (e) {
      logError('Attach additional place data', e)
      return places
    }
  }

  /**
   * Get additional google place details.
   *
   * @param google Google Maps API instance prepared in GoogleMapApiWrapper component
   * @param service Google Maps PlacesService
   * @param placeId Place ID for which to get mode details
   * @return {Promise<unknown>}
   */
  getPlaceDetails = (google, service, placeId) =>
    new Promise(resolve => {
      try {
        service.getDetails(
          {
            placeId: placeId,
            fields: ['international_phone_number'],
          },
          (details, detailsStatus) => {
            if (detailsStatus !== google.maps.places.PlacesServiceStatus.OK) {
              resolve({})
            }

            // TODO: PAT-2318 Get distance (it would require extending API key, so check if we really need it)
            //  https://developers.google.com/maps/documentation/distance-matrix/overview

            resolve(details)
          },
        )
      } catch (e) {
        logError('Get place details', e)
        resolve({})
      }
    })

  /**
   * Place markers/pins on the map for found places.
   *
   * @param {object} map Google map instance
   * @param {array} places Places found for search query (including additional custom data)
   * @param {object<{ lat: number, lng: number}>} latLng Current or found location
   * @returns {array} places Return received places but with additional "mapMarker" property
   */
  createMarkers = (map, places, latLng) => {
    try {
      const { google } = this.props

      // If not prepared in GoogleMapApiWrapper
      if (!(google && google.maps)) {
        logError('google.maps not available in props on create markers', null, 0)
        return places
      }

      // Clear existing markers
      if (places && places.length > 0) {
        this.clearMarkers()
      }

      // Prepare bounds to set map zoom/center after all markers are placed
      const bounds = new google.maps.LatLngBounds()

      // Include current or found location in bounds
      bounds.extend(latLng)

      let markers = []

      // Loop through all found places to set markers
      for (let i = 0, place; (place = places[i]); i++) {
        // Place the marker
        const marker = new google.maps.Marker({
          map,
          icon: place.markerIcon,
          title: place.name,
          position: place.geometry.location,
          animation: google.maps.Animation.DROP,
        })

        markers.push(marker)

        // Handle clicking on map marker
        google.maps.event.addListener(marker, 'click', () => {
          this.onPlaceSelect(place, marker)
        })

        places[i] = {
          ...places[i],
          mapMarker: marker,
        }

        // Update map bounds (for zoom/center)
        bounds.extend(place.geometry.location)
      }

      // Cluster markers
      this.createMarkerClusters(google, map, markers)

      // Adjust map zoom/center based on placed markers
      if (places && places.length > 0) {
        map.fitBounds(bounds)

        // Wait for fitBounds() to complete
        const mapIdleListener = google.maps.event.addListener(map, 'idle', () => {
          // Pan the map if pan value was previously set (e.g. on list load)
          if (this.mapPanVertical > 0) {
            map.panBy(0, this.mapPanVertical)
          }

          google.maps.event.removeListener(mapIdleListener)
        })
      }

      return places
    } catch (e) {
      logError('Create markers', e)
      return places
    }
  }

  /**
   * Group / cluster markers which are close to each other.
   *
   * @param {object} google
   * @param {object} map Google map instance
   * @param {array} markers Markers/pins places on the map
   *
   * @returns {void}
   */
  createMarkerClusters = (google, map, markers) => {
    const doc = getKioskDocument()
    const clusterColor = getComputedStyle(doc.body).getPropertyValue('--chakra-colors-secondary')
    try {
      const markerCluster = new MarkerClusterer(map, markers, {
        styles: [
          {
            width: 36,
            height: 36,
            textLineHeight: 36,
            url:
              'data:image/svg+xml;charset=UTF-8,' +
              encodeURIComponent(
                `<svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
              <circle cx="18" cy="18" r="18" fill="${clusterColor}" />
            </svg>`,
              ),
            textColor: 'white',
            textSize: 16,
          },
        ],
        maxZoom: 15,
        // Smaller the number results in less clustering. Markers are 50x62, so 50 should ensure small overlaps
        gridSize: 50,
        clusterClass: styles.clusterClass,
      })

      google.maps.event.addListener(markerCluster, 'clusterclick', cluster => {
        this.onClusterSelect(cluster)
      })

      this.setState({
        markerCluster: markerCluster,
      })
    } catch (e) {
      logError('Create map marker clusters', e)
    }
  }

  /**
   * When pin/maker or item from the list are selected.
   *
   * @param {object} place Selected place. Should include the marker when calling from the list.
   * @param {object} marker Can be empty when calling from the list
   */
  onPlaceSelect = (place, marker = null) => {
    try {
      const { map } = this.state

      // Get marker from the place (our state)
      if (!marker && place.hasOwnProperty('mapMarker')) {
        marker = place.mapMarker
      }

      // Set new icon
      if (marker) {
        // Update icon of previously selected marker
        if (this.selectedMarker && this.selectedMarkerIcon) {
          this.selectedMarker.setIcon(this.selectedMarkerIcon)
        }

        // Save current marker and icon
        this.selectedMarker = marker
        this.selectedMarkerIcon = place.markerIcon

        // Update selected icon
        marker.setIcon(place.markerIconSelected)
      }

      // Save map position before place select
      this.mapPositions.push({
        zoom: map.getZoom(),
        pan: map.getCenter(),
      })

      this.setState(
        {
          selectedPlace: place,
        },
        () => {
          if (map.getZoom() < 17) {
            map.setZoom(17)
          }

          // Center the map to selected place
          map.panTo(place.geometry.location)

          // Adjust vertical position so that selected pin is 20px above selected popup
          const containerHeight = this.getRefHeight(this.googleMapContainerRef)
          const selectedPlaceHeight = this.getRefHeight(this.selectedPlaceRef)
          if (containerHeight > 0 && selectedPlaceHeight > 0) {
            // Height of visible part of the map
            const usableHeight = containerHeight - selectedPlaceHeight

            // Middle of visible part of the map
            map.panBy(0, Math.round(containerHeight / 2 - usableHeight / 2 - this.mapPanOffsetTop))
          }
        },
      )
    } catch (e) {
      logError('Place select', e)
    }
  }

  /**
   * When pin/maker cluster is selected.
   *
   * @param {object} cluster Selected cluster details
   */
  onClusterSelect = () => {
    try {
      const { map } = this.state

      // Save map position before cluster select
      this.mapPositions.push({
        zoom: map.getZoom(),
        pan: map.getCenter(),
      })
    } catch (e) {
      logError('Select cluster', e)
    }
  }

  /**
   * Clear existing markers from the map.
   *
   * @return {boolean}
   */
  clearMarkers = () => {
    try {
      const { markerCluster } = this.state

      // Clear out existing markers
      if (markerCluster && typeof markerCluster.clearMarkers === 'function') {
        markerCluster.clearMarkers()
      }

      this.setState({
        markerCluster: null,
      })

      return true
    } catch (e) {
      logError('Clear map markers')
      return false
    }
  }

  /**
   * Get previous map position details: zoom, pan/position/center...
   *
   * @return {null|{zoom: number, pan: object}}
   */
  getPrevMapPosition = () => {
    try {
      if (isEmpty(this.mapPositions)) {
        return null
      }

      // Take last map position and remove that last position from array
      const prevMapState = this.mapPositions.pop()

      if (isEmpty(prevMapState)) {
        return null
      }

      return prevMapState
    } catch (e) {
      return null
    }
  }

  /**
   *
   * Clear selected place popup and custom "selected marker".
   *
   */
  closeSelectedPlace = () => {
    const { map } = this.state

    if (this.selectedMarker && this.selectedMarkerIcon) {
      this.selectedMarker.setIcon(this.selectedMarkerIcon)

      this.selectedMarker = null
      this.selectedMarkerIcon = null
    }

    // Reposition the map to the previous step
    const prevMapPosition = this.getPrevMapPosition()
    if (prevMapPosition && prevMapPosition.hasOwnProperty('zoom')) {
      map.setZoom(prevMapPosition.zoom)
    }
    if (prevMapPosition && prevMapPosition.hasOwnProperty('pan')) {
      map.panTo(prevMapPosition.pan)
    }

    this.setState({
      selectedPlace: null,
    })
  }

  /**
   * Place marker/pin for user's current location.
   *
   * @param {object} map Google map instance
   * @param {object<{ lat: number, lng: number}>} latLng
   * @returns {void|boolean}
   */
  setCurrentLocationMarker = (map, latLng) => {
    try {
      const { google } = this.props

      if (!map) {
        return false
      }

      // If not prepared in GoogleMapApiWrapper
      if (!(google && google.maps)) {
        logError('google.maps not available in props on set current location marker', null, 0)
        return false
      }

      // Clear existing marker
      if (this.currentLocationMarker && typeof this.currentLocationMarker.setMap === 'function') {
        this.currentLocationMarker.setMap(null)
      }

      // Set new current location marker
      this.currentLocationMarker = new google.maps.Marker({
        map,
        icon: {
          url: `${this.apiSrcImagesUrl}/map/location-current.svg`,
          size: new google.maps.Size(24, 24),
          scaledSize: new google.maps.Size(24, 24),
          // Where is the "center" of this icon ({halfOfWidth}, {halfOfHeight})
          anchor: new google.maps.Point(12, 12),
        },
        position: latLng,
      })
    } catch (e) {
      logError('Set current location marker', e)
    }
  }

  /**
   * Handle clicking on "find my location" button which will:
   * - get current location,
   * - re-center the map,
   * - update marker/pin for current position,
   * - get new results.
   *
   * @param {function} afterLoadCallback
   * @returns {void|boolean}
   */
  onFindMyLocation = async afterLoadCallback => {
    try {
      const { map } = this.state

      // Map should already be initialized
      if (!map) {
        this.initMap()
        return false
      }

      // Clear current location's search radius
      this.clearCurrentLocationRadiusCircle()

      // Get current user's location
      const location = await this.getLocationDetails(false)

      const latLng = location.latLng

      this.setState({
        myLocationType: location.locationType,
      })

      if (!latLng) {
        afterLoadCallback()
        return false
      }

      this.onLocationFound(map, latLng, afterLoadCallback)
    } catch (e) {
      logError('Find my location', e)
    }
  }

  /**
   * Save last known location to storage. To be used as cache.
   *
   * @param {('geo'|'alt')} locationType
   * @param {{lng: number, lat: number}} latLng
   * @param {int} expiresInSeconds
   */
  saveLocationToStorage = (locationType, latLng, expiresInSeconds) => {
    Storage.setItem(
      'lastKnownLocation',
      JSON.stringify({
        type: locationType,
        location: latLng,
        expiresAt: new Date().getTime() + expiresInSeconds * 1000,
      }),
    )
  }

  /**
   * Get cached location, if it didn't expire.
   *
   * @return {{latLng: null|{lng: number, lat: number}, locationType: string}}
   */
  getLocationFromStorage = () => {
    const defaultResponse = {
      latLng: null,
      locationType: 'unknown',
    }

    try {
      const cachedLocation = Storage.getItem('lastKnownLocation')
      const cached = cachedLocation ? JSON.parse(cachedLocation) : {}
      if (!(cached && cached.expiresAt && cached.location)) {
        return defaultResponse
      }

      // Return cached location if cache is not old
      if (new Date().getTime() < cached.expiresAt) {
        return {
          latLng: cached.location,
          locationType: cached.type,
        }
      }

      return defaultResponse
    } catch (e) {
      return defaultResponse
    }
  }

  /**
   * Log why we couldn't find location. This is not supported in iOS/Safari,
   * so we can't use it to show appropriate message to user, but only to log it.
   *
   * @return {Promise<unknown>}
   */
  checkGeolocationPermissions = () =>
    new Promise(resolve => {
      try {
        if (
          !(
            geoLocationExists() &&
            navigator.geolocation &&
            navigator.permissions &&
            navigator.permissions.query
          )
        ) {
          resolve(null)
        }

        navigator.permissions.query({ name: 'geolocation' }).then(
          result => {
            if (result.state !== 'denied') {
              logMessage('Location enabled')
            } else {
              // Location disabled in browser
              logError('Location disabled in browser', null, 0)
              resolve(null)
            }
          },
          error => {
            // Browser doesn't support querying for permissions
            logError('Browser does not support querying for permissions', error, 0)
            resolve(null)
          },
        )
      } catch (e) {
        logError('Verify geolocation permissions. It is fine if this fails in safari.', e, 0)
        resolve(null)
      }
    })

  /**
   * Try to find user's current location from browser.
   *
   * Also saves found location to cache.
   *
   * @return {Promise<null|{lng: number, lat: number}>} Returns null if blocked in OS or browser
   */
  getBrowserGeolocation = () =>
    new Promise(resolve => {
      try {
        const { intl } = this.props

        this.locationError = {}

        if (!(geoLocationExists() && navigator.geolocation)) {
          resolve(null)
        }

        navigator.geolocation.getCurrentPosition(
          position => {
            // Location found
            const latLng = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            }

            this.saveLocationToStorage('geo', latLng, this.geoLocationCacheSeconds)

            resolve(latLng)
          },
          error => {
            // Location disabled
            logError('Location disabled', error, 0)

            this.locationError = {
              title: intl.formatMessage(intlMessages.allowLocationTitle),
              body: intl.formatMessage(intlMessages.allowLocationMessage),
            }

            resolve(null)
          },
          {
            enableHighAccuracy: false,
            // Wait 2.5 sec to find location
            timeout: 2500,
            // Cache location
            maximumAge: this.geoLocationCacheSeconds * 1000,
          },
        )
      } catch (e) {
        logError('Get current location', e, 0)

        // Log why we didn't find location
        this.checkGeolocationPermissions()

        resolve(null)
      }
    })

  /**
   * Try to get approximate location with external API.
   *
   * Also saves found location to cache.
   *
   * @return {Promise<null|{lng: number, lat: number}>} Returns null if location not found
   */
  getAltLocation = async () => {
    try {
      const instance = axios.create({ timeout: 2000 })
      const response = await instance.get('https://ipinfo.io/geo')

      if (!(response && response.hasOwnProperty('data')) && response.data.hasOwnProperty('loc')) {
        return null
      }
      if (isEmpty(response.data.loc)) {
        return null
      }

      const loc = response.data.loc.split(',')

      const latLng = {
        lat: parseFloat(loc[0]),
        lng: parseFloat(loc[1]),
      }

      this.saveLocationToStorage('alt', latLng, this.approximateLocationCacheSeconds)

      return latLng
    } catch (e) {
      logError('Get approximate location', e)
      return null
    }
  }

  /**
   * Tries to find location in this order:
   * - cache (unless disabled with `useCache` param)
   * - browser's geolocation (precise)
   * - external API (less precise, probably based on IP address)
   *
   * @param {boolean} useCache Used for skipping cache when using "find my location" button
   * @return {Promise<{locationType: string, latLng: {lng: number, lat: number}}|{locationType: string, latLng: null}>}
   */
  getLocationDetails = async (useCache = true) => {
    try {
      if (useCache) {
        // TODO: PAT-3207 Check if we need to "force" use cache when saved location is precise ('geo'),
        //  this should then return cached type as well

        // Return cached version if not expired
        const cachedLocation = this.getLocationFromStorage()
        if (cachedLocation && cachedLocation.latLng) {
          return {
            latLng: cachedLocation.latLng,
            locationType: LocationType.Cache,
          }
        }
      }

      // Try to find precise location from browser
      const geoLocation = await this.getBrowserGeolocation()
      if (geoLocation) {
        return {
          latLng: geoLocation,
          locationType: LocationType.Geo,
        }
      }

      // Try to find approximate location with external API
      const altLocation = await this.getAltLocation()
      if (altLocation) {
        return {
          latLng: altLocation,
          locationType: LocationType.Alt,
        }
      }

      // Show error only if we didn't return location above (geo or alternative/fallback) and error was collected
      if (!isEmpty(this.locationError)) {
        this.props.toast({
          title: this.locationError.title,
          description: this.locationError.body,
          status: 'warning',
          duration: 8000,
        })
      }

      return {
        latLng: null,
        locationType: LocationType.Unknown,
      }
    } catch (e) {
      logError('Get location with fallback', e, 0)
      return {
        latLng: null,
        locationType: LocationType.Unknown,
      }
    }
  }

  /**
   * Called when sliding list is loaded.
   *
   * @param {number} mapPanVertical How much to move the map to the top or bottom
   */
  handleListLoad = (mapPanVertical = 0) => {
    if (mapPanVertical > 0) {
      // Just set it and it will be picked up after the map is ready (idle)
      this.mapPanVertical = mapPanVertical - this.mapPanOffsetTop
    }
  }

  /**
   * Get height of referenced DOM element.
   *
   * @param ref For which DOM element to get the height
   * @return {number}
   */
  getRefHeight = ref => {
    try {
      if (ref && ref.current) {
        return parseInt(ref.current.offsetHeight)
      }
      return 0
    } catch (e) {
      return 0
    }
  }

  render() {
    const { intl, isWidget, google, notificationHeight, isNotificationHidden } = this.props

    const { map, places, selectedPlace, myLocationType, isBackdropVisible } = this.state

    let selectedPlaceDirectionLink = ''
    if (selectedPlace && selectedPlace.place_id) {
      selectedPlaceDirectionLink += 'https://www.google.com/maps/dir/?api=1'
      selectedPlaceDirectionLink += `&destination=${encodeURIComponent(selectedPlace.name)}`
      selectedPlaceDirectionLink += `&destination_place_id=${selectedPlace.place_id}`
    }

    return (
      <div className={styles.googleMapContainer} ref={this.googleMapContainerRef}>
        {isBackdropVisible && <div className={styles.backdrop} />}
        {google && map ? (
          <GoogleMapFindBar
            google={google}
            map={map}
            myLocationType={myLocationType}
            onNewLocationFound={this.onLocationFound}
            onNewLocationNotFound={this.onLocationNotFound}
            onNewLocationStart={this.onNewLocationStart}
            onFindMyLocation={this.onFindMyLocation}
          />
        ) : (
          ''
        )}
        <div ref={this.googleMapRef} className={map ? styles.fullHeight : ''} />
        <GoogleMapList
          isVisible={!selectedPlace}
          containerRef={this.googleMapContainerRef}
          isWidget={isWidget}
          items={places}
          onItemSelect={this.onPlaceSelect}
          onAfterLoad={this.handleListLoad}
          notificationHeight={notificationHeight}
          isNotificationHidden={isNotificationHidden}
        />
        {!map && <Loader styleVariation='staticWithHeight' className={styles.fullHeight} />}
        {selectedPlace && (
          <div
            className={cx({
              [styles.selectedPlace]: true,
              'kiosk--chakra-colors-card-background': true,
            })}
            ref={this.selectedPlaceRef}
          >
            <div className={styles.selectedPlaceInner}>
              <SvgIcon
                type='RemoveThin'
                className={styles.selectedPlaceClose}
                onClick={this.closeSelectedPlace}
              />
              <div className={styles.selectedPlaceDetails}>
                {selectedPlace.placeIconName && (
                  <div className={styles.selectedPlaceIconHolder}>
                    <Box opacity={0.5}>
                      <FontAwesomeIconComponent
                        className={styles.selectedPlaceIcon}
                        icon={selectedPlace.placeIconName}
                        size='sm'
                      />
                    </Box>
                  </div>
                )}
                <div>
                  <Text fontSize={'18px'} fontWeight={500}>
                    {selectedPlace.name}
                  </Text>
                  <PlaceRating place={selectedPlace} />
                  {selectedPlace.vicinity ? (
                    <div className={styles.selectedPlaceAddress}>
                      <Box opacity={0.5}>
                        <FontAwesomeIconComponent icon={['far', 'location-dot']} size='xs' />
                      </Box>
                      <Text mt={'3px'} ml={'3px'} fontSize={'13px'}>
                        {selectedPlace.vicinity}
                      </Text>
                    </div>
                  ) : null}
                </div>
              </div>

              <Flex>
                {selectedPlace.international_phone_number && (
                  <LinkButton
                    className={styles.linkButtons}
                    href={`tel:${selectedPlace.international_phone_number}`}
                    iconType={['far', 'phone-arrow-up-right']}
                  >
                    {intl.formatMessage(intlMessagesApp.appGlobalPhonesCall)}
                  </LinkButton>
                )}
                {!isEmpty(selectedPlaceDirectionLink) && (
                  <LinkButton
                    href={selectedPlaceDirectionLink}
                    iconType={['far', 'diamond-turn-right']}
                  >
                    {intl.formatMessage(intlMessagesApp.appGlobalDirections)}
                  </LinkButton>
                )}
              </Flex>
            </div>
          </div>
        )}
      </div>
    )
  }
}

GoogleMapContainer.propTypes = {
  isWidget: PropTypes.bool,
  mapFilterValue: PropTypes.string.isRequired,
  mapFilterType: PropTypes.string.isRequired,
}

export default GoogleMapApiWrapper({
  googleApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
})(injectIntl(withToast(GoogleMapContainer)))
